import { preventImplicitSubmit } from "../../../../../app/javascript/keyboard/form_keys"
import { fetchVariantDetails } from "utils/variant_details"
import {Controller} from "@hotwired/stimulus"
import {showToast} from "utils/toast";

export default class extends Controller {
    static targets = ["form", "itemTemplate", "itemsContainer", "itemsPanel", "subtotal", "tax", "total", "itemVariantCombobox"]
    static values = {status: String, itemVariantsUrl: String, itemVariantDetailsUrl: String, taxesUrl: String, postUrl: String}

    connect() {
        this.itemVariantsById = {}
        this.itemContent = this.itemTemplateTarget.innerHTML

        this.clientInput = this.formTarget.querySelector("input[name='credit_note[client_id]']")
        this.clientSelectInput = this.formTarget.querySelector("#credit_note_client_id_hw_combobox")
        this.taxModeInput = this.formTarget.querySelector("select[name='credit_note[tax_mode]']")
        this.currencyInput = this.formTarget.querySelector("select[name='credit_note[currency]']")
        this.postButton = this.formTarget.querySelector("#btn_post")

        this.isDirty = false

        this.formTarget.addEventListener("input", () => {
            this.isDirty = true
        })

        this.formTarget.addEventListener("change", () => {
            this.isDirty = true
        })

        this.formTarget.addEventListener("keydown", preventImplicitSubmit)


        if (this.clientInput?.value) {
            this.loadClientData(this.clientInput.value)
                .then(() => {
                    this.initializeExistingItems()
                    this.calculateCreditNote()
                })
        } else {
            if (this.clientSelectInput) this.clientSelectInput.focus()
        }
    }

    addItem(event) {
        if (event) event.preventDefault()
        const content = this.itemContent.replace(/NEW_RECORD/g, new Date().getTime())
        this.itemsContainerTarget.insertAdjacentHTML("beforeend", content)
        const lastItem = this.itemsContainerTarget.querySelector("tr.item-container:last-child")
        this.configureItemVariantCombobox(lastItem)
        const firstInput = lastItem.querySelector("input")
        firstInput.focus()
    }

    removeItem(event) {
        event.preventDefault()

        const wrapper = event.target.closest(".item-container")
        wrapper.remove()

        this.calculateCreditNote()
    }

    clearItems() {
        this.itemsContainerTarget.innerHTML = ""
    }

    changeClient(event) {
        if (event) event.preventDefault()

        this.itemVariantsById = {}
        this.clearItems()

        if (!this.clientInput.value) {
            this.itemsPanelTarget.classList.add("d-none")
            return
        }

        this.loadClientData(this.clientInput.value)
            .then(() => {
                this.addItem()
            })
    }

    loadClientData(clientId) {
        const taxesUrl = this.taxesUrlValue + "?client_id=" + clientId

        return fetch(taxesUrl, {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
            },
            credentials: "same-origin"
        })
            .then(response => response.json())
            .then(taxData => {
                this.taxes = taxData
                this.itemsPanelTarget.classList.remove("d-none")
            })
            .catch(error => console.error(error))
    }


    selectVariant(event) {
        if (event) event.preventDefault()
        setTimeout(() => {
            const item = event.target.closest("tr.item-container")
            const variantInput = item.querySelector("input[name$='[item_variant_id]']")
            const variantID = variantInput.value
            const unitPriceInput = item.querySelector("input[name$='[unit_price]']")
            if (!variantID) {
                unitPriceInput.value = ""
                this.calculateCreditNote()
                return
            }

            this.ensureVariantDetails(variantID)
                .then(variant => {
                    if (!variant) return

                    unitPriceInput.value = variant.price
                    unitPriceInput.dispatchEvent(new Event('change'))
                    this.calculateCreditNote()

                    if (item.parentElement.lastElementChild === item) this.addItem()
                })
                .catch(error => console.error(error))
        }, 10)
    }

    calculateCreditNote(event) {
        if (event) event.preventDefault()

        const items = this.collectItems()
        const taxMode = this.taxModeInput.value

        let subtotal = 0
        let totalTax = 0
        let grossTotal = 0

        items.forEach(item => {
            const result = this.calculateLine(item, taxMode)
            if (!result) return

            subtotal += result.subtotal
            totalTax += result.tax
            grossTotal += result.total

            this.renderLine(item.node, result)
        })

        subtotal = this.round(subtotal)
        totalTax = this.round(totalTax)
        grossTotal = this.round(grossTotal)

        this.renderTotals(subtotal, totalTax, grossTotal)
    }

    collectItems() {
        const nodes = this.itemsContainerTarget.querySelectorAll("tr.item-container")

        return [...nodes]
            .map(node => {
                if (node.style.display === "none") return null

                const idInput = node.querySelector("input[name$='[id]']")
                const variantId = Number(node.querySelector("input[name$='[item_variant_id]']").value)
                const quantity = Number(node.querySelector("input[name$='[quantity]']").value)
                const unitPrice = Number(node.querySelector("input[name$='[unit_price]']").value)

                if (!variantId || quantity <= 0 || unitPrice < 0) return null

                const variant = this.variantById(variantId)
                if (!variant) return null

                return {
                    node,
                    id: idInput ? idInput.value : null,
                    quantity,
                    unitPrice,
                    variant
                }
            })
            .filter(Boolean)
    }

    calculateLine(item, taxMode) {
        const quantity = item.quantity
        const unitPrice = item.unitPrice

        // full precision base
        const rawBase = unitPrice * quantity
        const roundedBase = this.round(rawBase)

        const applicableTaxes = this.resolveTaxesForVariant(item.variant)
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))

        let taxes = []
        let lineTaxTotal = 0

        // ✅ NO TAX MODE (or no taxes found)
        if (!taxMode || taxMode === "no_tax" || applicableTaxes.length === 0) {
            return {
                subtotal: roundedBase,
                tax: 0,
                total: roundedBase,
                taxes: []
            }
        }

        // ✅ TAX EXCLUSIVE
        if (taxMode === "tax_exclusive") {
            let runningBase = rawBase

            applicableTaxes.forEach(tax => {
                const rate = Number(tax.rate) / 100
                const baseAmount = tax.compound ? runningBase : rawBase

                const taxAmount = this.round(baseAmount * rate)

                taxes.push({
                    ...tax,
                    base: this.round(baseAmount),
                    amount: taxAmount
                })

                lineTaxTotal += taxAmount

                if (tax.compound) {
                    runningBase += taxAmount
                }
            })

            const subtotal = roundedBase
            const total = this.round(subtotal + lineTaxTotal)

            return {
                subtotal,
                tax: this.round(lineTaxTotal),
                total,
                taxes
            }
        }

        // ✅ TAX INCLUSIVE
        if (taxMode === "tax_inclusive") {
            let netBase = rawBase

            const reversed = [...applicableTaxes].reverse()

            reversed.forEach(tax => {
                const rate = Number(tax.rate) / 100
                const divisor = 1 + rate

                const baseWithoutTax = netBase / divisor
                const taxAmount = this.round(netBase - baseWithoutTax)

                taxes.push({
                    ...tax,
                    base: this.round(baseWithoutTax),
                    amount: taxAmount
                })

                netBase = baseWithoutTax
                lineTaxTotal += taxAmount
            })

            taxes.reverse()

            const subtotal = this.round(rawBase - lineTaxTotal)
            const total = roundedBase

            return {
                subtotal,
                tax: this.round(lineTaxTotal),
                total,
                taxes
            }
        }

        // ✅ FINAL SAFETY FALLBACK (prevents undefined ever)
        return {
            subtotal: roundedBase,
            tax: 0,
            total: roundedBase,
            taxes: []
        }
    }

    renderLine(node, result) {
        const formatter = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: this.currencyCode()
        })

        const taxCodesEl = node.querySelector("[data-finance-credit-note-line-target='taxCodes']")
        const taxAmountEl = node.querySelector("[data-finance-credit-note-line-target='taxAmount']")
        const lineTotalEl = node.querySelector("[data-finance-credit-note-line-target='lineTotal']")

        if (taxCodesEl) {
            taxCodesEl.innerText = result.taxes.map(t => t.code).join(", ") || "-"
        }

        if (taxAmountEl) {
            taxAmountEl.innerText = formatter.format(result.tax)
        }

        if (lineTotalEl) {
            lineTotalEl.innerText = formatter.format(result.total)
        }
    }

    resolveTaxesForVariant(variant) {
        const unique = {}
        const results = []

        this.taxes.forEach(tax => {
            if (variant.taxes.includes(tax.id) && !unique[tax.id]) {
                unique[tax.id] = true
                results.push(tax)
            }
        })

        return results
    }

    renderTotals(subtotal, tax, total) {
        const formatter = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: this.currencyCode()
        })

        this.subtotalTarget.innerText = formatter.format(subtotal)
        this.taxTarget.innerText = formatter.format(tax)
        this.totalTarget.innerText = formatter.format(total)
    }

    round(amount) {
        return Math.round((amount + Number.EPSILON) * 100) / 100
    }

    currencyCode() {
        return this.currencyInput.value
    }

    initializeExistingItems() {
        const detailRequests = []

        this.itemsContainerTarget.querySelectorAll("tr.item-container").forEach(item => {
            this.configureItemVariantCombobox(item)

            const variantInput = item.querySelector("input[name$='[item_variant_id]']")
            if (!variantInput || !variantInput.value) return

            detailRequests.push(this.ensureVariantDetails(variantInput.value))
        })

        return Promise.all(detailRequests)
    }

    ensureVariantDetails(variantId) {
        if (!this.clientInput?.value) return Promise.resolve(null)

        const variant = this.variantById(variantId)
        if (variant?.stock_entries_loaded) return Promise.resolve(variant)

        const url = `${this.itemVariantsUrlValue}?client_id=${this.clientInput.value}`
        const requestKey = `${url}#${variantId}`
        this.variantDetailRequests ||= new Map()
        if (this.variantDetailRequests.has(requestKey)) return this.variantDetailRequests.get(requestKey)
        const request = fetchVariantDetails(url, variantId)
            .then(data => {
                this.itemVariantsById[data.id] = data
                return data
            })
            .finally(() => this.variantDetailRequests.delete(requestKey))
        this.variantDetailRequests.set(requestKey, request)
        return request
    }

    configureItemVariantCombobox(item) {
        const combobox = item.querySelector("[data-finance-credit-note-target='itemVariantCombobox']")
        if (!combobox) return

        const fieldset = combobox.closest(".hw-combobox")
        if (!fieldset) return

        const serverDisabled = combobox.dataset.serverDisabled === "true"

        if (this.clientInput?.value) {
            fieldset.dataset.hwComboboxAsyncSrcValue = `${this.itemVariantsUrlValue}?client_id=${encodeURIComponent(this.clientInput.value)}`
            combobox.disabled = serverDisabled
        } else {
            fieldset.dataset.hwComboboxAsyncSrcValue = ""
            combobox.disabled = true
        }
    }

    variantById(variantId) {
        return this.itemVariantsById[Number(variantId)] || null
    }

    markClean() {
        this.isDirty = false
    }

    post(event) {
        if (event) event.preventDefault()
        
        if (!this.postUrlValue) return

        if (this.isDirty) {
            alert("You have unsaved changes. Please save before posting.")
            return
        }

        fetch(this.postUrlValue, {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
            },
            credentials: "same-origin"
        })
            .then(response => response.json())
            .then(data => {
                if (!data.ok) {
                    throw new Error(data.error || "Failed to generate POs")
                }
                this.markClean()
                Turbo.visit(window.location.href, { action: "replace" })
                showToast("Credit note posted.", "success")
            })
            .catch(error => {
                showToast(error)
            })
    }
}
